/**
 * Dados dos bairros do Rio de Janeiro para páginas locais de SEO.
 * Cada bairro tem conteúdo 100% único para evitar canibalização.
 * Reutiliza tipos e SERVICE_LABELS de bairros.ts (Brasília).
 */

import type { Bairro, BairroService, ServiceSlug } from "./bairros";
export type { Bairro, BairroService };
export { SERVICE_LABELS, SERVICOS_PRIORITARIOS, ATENDIMENTO_STEPS, ALL_SERVICE_SLUGS } from "./bairros";
import { SERVICE_LABELS, ALL_SERVICE_SLUGS } from "./bairros";
export { BASE_URL } from "./bairros";
import { BASE_URL } from "./bairros";

/* ─── Dados operacionais do Rio ─── */
export const RIO_PHONE = "+5521968500754";
export const RIO_PHONE_DISPLAY = "(21) 96850-0754";
export const RIO_ADDRESS = {
  street: "Travessa Ururai, 111",
  neighborhood: "Madureira",
  city: "Rio de Janeiro",
  state: "RJ",
  postalCode: "21511-420",
  country: "BR",
};

/* ─── Priority data for internal linking strategy ─── */
export const BAIRROS_PRIORITARIOS_RIO: string[] = [
  "barra-da-tijuca", "copacabana", "ipanema", "tijuca", "madureira",
];

/* ─── 10 Bairros ─── */
export const bairrosRio: Bairro[] = [
  /* 1 — BARRA DA TIJUCA */
  {
    slug: "barra-da-tijuca",
    name: "Barra da Tijuca",
    h1: "Dedetizadora na Barra da Tijuca — Controle de Pragas para Condomínios e Casas",
    metaTitle: "Dedetizadora na Barra da Tijuca | Orçamento Grátis",
    metaDescription: "Dedetizadora na Barra da Tijuca com garantia de resultado. Atendemos condomínios, casas e comércios. Orçamento grátis: (21) 96850-0754.",
    keyword: "dedetizadora na Barra da Tijuca",
    context: "A Barra da Tijuca é a maior região de crescimento imobiliário do Rio, com condomínios fechados, shoppings e áreas de restinga. A proximidade da lagoa de Marapendi e a abundância de áreas verdes favorecem infestações de mosquitos, baratas e ratos. Condomínios com piscinas, salas de lixo e jardins internos demandam controle periódico contra pragas durante o ano inteiro, especialmente no verão carioca.",
    topServices: ["Dedetização de condomínios", "Desratização em casas", "Descupinização de residências"],
    faq: [
      { question: "Vocês atendem condomínios na Barra da Tijuca?", answer: "Sim. Temos equipe dedicada para a Barra da Tijuca e atendemos condomínios de todos os portes. Agendamento pelo WhatsApp com visita em até 24h." },
      { question: "É seguro dedetizar apartamento com crianças?", answer: "Totalmente seguro. Usamos produtos aprovados pela ANVISA, sem cheiro e de baixa toxicidade, seguros para crianças e pets." },
      { question: "Quanto custa dedetização na Barra da Tijuca?", answer: "O valor varia conforme o tamanho do imóvel. Para apartamentos de 2 quartos, o investimento começa em torno de R$200. Solicite orçamento grátis." },
      { question: "Vocês fazem controle de mosquitos na Barra?", answer: "Sim. Oferecemos controle de mosquitos com nebulização e larvicida, ideal para áreas próximas à lagoa e condomínios com jardins." },
    ],
    services: [
      {
        slug: "dedetizacao",
        keyword: "dedetização na Barra da Tijuca",
        h1: "Dedetização na Barra da Tijuca — Elimine Baratas e Insetos do Seu Condomínio",
        metaTitle: "Dedetização na Barra da Tijuca | Resultado Garantido",
        metaDescription: "Dedetização profissional na Barra da Tijuca para condomínios e casas. Produtos seguros, sem cheiro. Garantia de resultado. Ligue: (21) 96850-0754.",
        localContext: "Os condomínios da Barra da Tijuca possuem grandes áreas comuns com jardins, piscinas e playgrounds que favorecem o surgimento de baratas e formigas. A proximidade de restingas e lagoas traz mosquitos e insetos noturnos. Nosso tratamento inclui gel nas áreas internas e pulverização residual perimetral, criando uma barreira que protege o condomínio mesmo em períodos de chuva intensa.",
        infestationSigns: [
          "Baratas aparecendo nas áreas comuns à noite",
          "Formigas em trilha na cozinha e despensa",
          "Mosquitos em excesso perto de áreas alagadas",
          "Insetos saindo por ralos e frestas de tubulação",
        ],
        serviceProcess: [
          "Inspeção técnica gratuita no condomínio na Barra da Tijuca",
          "Aplicação de gel em pontos estratégicos (cozinha, banheiros, ralos)",
          "Pulverização sem cheiro em áreas comuns e perimetrais",
          "Garantia de 3 a 6 meses com revisão gratuita",
        ],
        faq: [
          { question: "A dedetização no condomínio da Barra precisa evacuar o imóvel?", answer: "Não. Nossa técnica com gel e produtos sem cheiro permite que moradores permaneçam no local durante e após a aplicação." },
          { question: "Quanto tempo demora a dedetização de um apartamento na Barra?", answer: "Em média 40 a 60 minutos para apartamentos de 2 quartos. Coberturas e casas podem levar até 2 horas." },
          { question: "As baratas voltam depois da dedetização?", answer: "Nosso tratamento cria uma barreira residual que impede a reinfestação por até 6 meses. Oferecemos revisão gratuita na garantia." },
          { question: "Vocês emitem certificado de dedetização?", answer: "Sim. Emitimos certificado com validade para condomínios e estabelecimentos comerciais conforme exigências da Vigilância Sanitária." },
        ],
        trustProof: "Ampla experiência com condomínios e casas na Barra da Tijuca.",
      },
      {
        slug: "desratizacao",
        keyword: "desratização na Barra da Tijuca",
        h1: "Desratização na Barra da Tijuca — Elimine Ratos de Condomínios e Casas",
        metaTitle: "Desratização na Barra da Tijuca | Sem Ratos",
        metaDescription: "Desratização profissional na Barra da Tijuca. Eliminamos ratos de garagens, lixeiras e áreas comuns. Métodos seguros. Orçamento grátis: (21) 96850-0754.",
        localContext: "As áreas verdes da Barra da Tijuca e a proximidade de lagoas criam ambiente propício para roedores. Condomínios com grandes áreas comuns, lixeiras coletivas e jardins sofrem com ratos que buscam alimento e abrigo. Nossa abordagem combina iscas anticoagulantes em porta-iscas de segurança com vedação de pontos de entrada nas edificações.",
        infestationSigns: [
          "Fezes escuras encontradas na garagem ou depósito",
          "Marcas de roedura em fios elétricos e embalagens",
          "Barulhos de roedores no forro durante a noite",
          "Trilhas de gordura ao longo de muros e tubulações",
        ],
        serviceProcess: [
          "Vistoria completa das áreas comuns e garagem na Barra da Tijuca",
          "Instalação de porta-iscas seguros em pontos estratégicos",
          "Vedação de pontos de entrada identificados na inspeção",
          "Monitoramento mensal com relatório ao síndico",
        ],
        faq: [
          { question: "Os porta-iscas são seguros para pets e crianças?", answer: "Sim. Utilizamos porta-iscas de segurança com chave, impossíveis de serem abertos por crianças ou animais domésticos." },
          { question: "Quanto tempo leva para eliminar ratos do condomínio?", answer: "Normalmente 15 a 30 dias para controle total, com monitoramento contínuo para evitar reinfestação." },
          { question: "Vocês atendem casas na Barra da Tijuca?", answer: "Sim. Atendemos casas, condomínios horizontais e verticais com planos mensais de controle." },
          { question: "Ratos podem subir nos andares superiores?", answer: "Sim. Ratos de telhado escalam tubulações e acessam andares superiores. Nosso tratamento inclui vedação dos pontos de acesso." },
        ],
        trustProof: "Experiência consolidada com condomínios na Barra da Tijuca.",
      },
      {
        slug: "descupinizacao",
        keyword: "descupinização na Barra da Tijuca",
        h1: "Descupinização na Barra da Tijuca — Proteja Seus Móveis e Estruturas",
        metaTitle: "Descupinização na Barra da Tijuca | Cupins Nunca Mais",
        metaDescription: "Descupinização profissional na Barra da Tijuca. Eliminamos cupins de madeira e solo. Garantia estendida. Ligue: (21) 96850-0754.",
        localContext: "Casas e coberturas na Barra da Tijuca frequentemente utilizam madeira nobre em decks, pergolados e mobiliário externo. A umidade do clima litorâneo e a proximidade de áreas de restinga favorecem cupins subterrâneos e de madeira seca. Nosso diagnóstico identifica a espécie e aplica o tratamento correto: calda cupinicida para subterrâneos e gel para cupins de madeira seca.",
        infestationSigns: [
          "Pó fino de madeira (serragem) em rodapés e decks",
          "Orifícios pequenos em móveis e portas de madeira",
          "Túneis de barro em paredes ou pilares da garagem",
          "Madeira que soa oca ao bater",
        ],
        serviceProcess: [
          "Inspeção com detector de umidade para localizar colônias",
          "Identificação da espécie de cupim (madeira seca ou subterrâneo)",
          "Aplicação de calda cupinicida ou gel conforme diagnóstico",
          "Garantia de 2 a 5 anos com vistorias semestrais",
        ],
        faq: [
          { question: "Cupins atacam decks e pergolados na Barra?", answer: "Sim. A madeira exposta ao clima litorâneo é alvo frequente. Recomendamos tratamento preventivo e curativo." },
          { question: "Quanto custa a descupinização na Barra da Tijuca?", answer: "Depende da extensão da infestação. Para apartamentos, a partir de R$400. Solicite vistoria gratuita." },
          { question: "A descupinização tem garantia?", answer: "Sim. Oferecemos garantia de 2 a 5 anos dependendo do tipo de tratamento, com vistorias periódicas incluídas." },
          { question: "Preciso sair do apartamento durante o tratamento?", answer: "Para cupins de madeira seca (gel), não. Para tratamento com calda em área externa, recomendamos aguardar 2 horas." },
        ],
        trustProof: "Experiência comprovada no tratamento de cupins na Barra da Tijuca.",
      },
    ],
  },

  /* 2 — COPACABANA */
  {
    slug: "copacabana",
    name: "Copacabana",
    h1: "Dedetizadora em Copacabana — Controle de Pragas para Prédios e Comércios",
    metaTitle: "Dedetizadora em Copacabana RJ | Orçamento Grátis",
    metaDescription: "Dedetizadora em Copacabana com mais de 20 anos de experiência. Atendemos prédios residenciais e comércios. Orçamento grátis: (21) 96850-0754.",
    keyword: "dedetizadora em Copacabana",
    context: "Copacabana é um dos bairros mais densamente povoados do mundo, com prédios antigos e milhares de unidades habitacionais em poucos quilômetros quadrados. A alta concentração de restaurantes, padarias e lanchonetes atrai baratas, ratos e formigas. Os edifícios mais antigos (décadas de 50-70) possuem tubulações de ferro fundido com frestas que facilitam a circulação de pragas entre andares. O clima quente e úmido do litoral carioca potencializa o ciclo reprodutivo dos insetos.",
    topServices: ["Dedetização de prédios antigos", "Desratização em restaurantes", "Controle de baratas"],
    faq: [
      { question: "Vocês atendem prédios antigos em Copacabana?", answer: "Sim. Temos experiência com prédios de todas as épocas em Copacabana, incluindo edifícios sem elevador das décadas de 50-70." },
      { question: "Copacabana tem mais baratas que outros bairros?", answer: "A alta densidade de comércios alimentícios e prédios antigos favorece infestações. Nosso controle inclui tratamento perimetral e gel nas unidades." },
      { question: "Vocês atendem restaurantes em Copacabana?", answer: "Sim. Oferecemos controle integrado de pragas para restaurantes com certificação para a Vigilância Sanitária." },
      { question: "Qual a frequência ideal de dedetização em Copacabana?", answer: "Para residências, a cada 4-6 meses. Para comércios alimentícios, mensalmente conforme exigência sanitária." },
    ],
    services: [
      {
        slug: "dedetizacao",
        keyword: "dedetização em Copacabana",
        h1: "Dedetização em Copacabana — Elimine Baratas e Insetos do Seu Prédio",
        metaTitle: "Dedetização em Copacabana RJ | Resultado Garantido",
        metaDescription: "Dedetização profissional em Copacabana para prédios e comércios. Produtos seguros, sem cheiro. Resultado garantido. Ligue: (21) 96850-0754.",
        localContext: "Os prédios de Copacabana sofrem com infestações recorrentes de baratas-de-esgoto que migram pela rede de galerias pluviais. Os andares térreos com comércios alimentícios são os mais afetados. Nosso tratamento inclui gel nas tubulações e pulverização residual nos rodapés, criando uma barreira que protege cada unidade mesmo em prédios antigos com infraestrutura comprometida.",
        infestationSigns: [
          "Baratas-de-esgoto aparecendo à noite na cozinha",
          "Formigas doceiras invadindo despensas e armários",
          "Insetos saindo pelos ralos e frestas de tubulação",
          "Baratas voando atraídas pela iluminação externa",
        ],
        serviceProcess: [
          "Inspeção técnica gratuita no prédio em Copacabana",
          "Aplicação de gel nos pontos críticos (pia, fogão, ralos)",
          "Pulverização sem cheiro em rodapés e áreas comuns",
          "Garantia de 3 a 6 meses com revisão gratuita",
        ],
        faq: [
          { question: "A dedetização cobre todo o prédio em Copacabana?", answer: "Sim. Oferecemos pacotes condominiais que incluem áreas comuns e apartamentos individuais." },
          { question: "Prédios antigos de Copacabana têm mais pragas?", answer: "Sim. Tubulações antigas com frestas facilitam a circulação. Nosso tratamento inclui vedação e barreira residual." },
          { question: "A dedetização atrapalha a rotina?", answer: "Não. Usamos produtos sem cheiro e de ação residual. A aplicação leva cerca de 45 minutos por apartamento." },
          { question: "Vocês emitem certificado?", answer: "Sim. Emitimos certificado válido para condomínios e comércios conforme exigências da Vigilância Sanitária." },
        ],
        trustProof: "Experiência consolidada com prédios e comércios em Copacabana.",
      },
      {
        slug: "desratizacao",
        keyword: "desratização em Copacabana",
        h1: "Desratização em Copacabana — Controle de Ratos em Prédios e Restaurantes",
        metaTitle: "Desratização em Copacabana RJ | Ratos Nunca Mais",
        metaDescription: "Desratização em Copacabana. Eliminamos ratos de prédios, restaurantes e comércios. Métodos seguros. Orçamento grátis: (21) 96850-0754.",
        localContext: "A rede de esgoto antiga de Copacabana e a alta concentração de restaurantes criam um habitat ideal para roedores. Ratos de esgoto (Rattus norvegicus) são frequentes nos subsolos e lixeiras coletivas. Nosso serviço combina iscas em porta-iscas seguros, armadilhas mecânicas e consultoria sobre gestão de resíduos para condomínios e comércios.",
        infestationSigns: [
          "Ratos vistos nos arredores das lixeiras à noite",
          "Marcas de roedura em sacos de lixo e embalagens",
          "Fezes de roedores no subsolo ou área de serviço",
          "Ruídos de roedores nas paredes durante a noite",
        ],
        serviceProcess: [
          "Diagnóstico com mapeamento de tocas e caminhos em Copacabana",
          "Instalação de porta-iscas seguros em pontos estratégicos",
          "Vedação de buracos e frestas em tubulações antigas",
          "Acompanhamento quinzenal por 60 dias",
        ],
        faq: [
          { question: "Ratos de esgoto sobem nos apartamentos em Copacabana?", answer: "Sim. Ratos acessam andares superiores por tubulações antigas. Nosso tratamento inclui vedação dos pontos de acesso." },
          { question: "Vocês atendem restaurantes em Copacabana?", answer: "Sim. Oferecemos planos mensais com emissão de certificado para a Vigilância Sanitária." },
          { question: "Quanto custa a desratização de um prédio?", answer: "Depende do tamanho. Oferecemos orçamento gratuito com vistoria técnica in loco." },
          { question: "A desratização é segura para moradores?", answer: "Totalmente. As iscas ficam em porta-iscas com chave, inacessíveis para pessoas e pets." },
        ],
        trustProof: "Experiência consolidada com prédios e restaurantes em Copacabana.",
      },
      {
        slug: "descupinizacao",
        keyword: "descupinização em Copacabana",
        h1: "Descupinização em Copacabana — Proteja Seus Móveis e Estruturas",
        metaTitle: "Descupinização em Copacabana | Cupins Nunca Mais",
        metaDescription: "Descupinização em Copacabana. Cupins em prédios antigos e mobiliário. Garantia estendida. Ligue: (21) 96850-0754.",
        localContext: "Prédios antigos de Copacabana possuem tacos de madeira, batentes e esquadrias que são alvo frequente de cupins de madeira seca. A umidade do litoral acelera a deterioração e favorece colônias. Nosso tratamento identifica a espécie e aplica gel nos pontos afetados, preservando pisos e estruturas históricas sem necessidade de remoção.",
        infestationSigns: [
          "Pó fino de madeira ao lado de rodapés e batentes",
          "Pequenos orifícios em tacos de madeira do piso",
          "Madeira que soa oca ao bater nos batentes",
          "Asas de cupins (revoada) perto de janelas à noite",
        ],
        serviceProcess: [
          "Inspeção completa de madeiras e estruturas no apartamento",
          "Identificação da espécie de cupim presente",
          "Aplicação de gel cupinicida nos pontos afetados",
          "Garantia de 2 a 5 anos com vistorias semestrais",
        ],
        faq: [
          { question: "Cupins atacam tacos de madeira em Copacabana?", answer: "Sim. Tacos de madeira em prédios antigos são alvo frequente. Nosso tratamento com gel preserva o piso sem remoção." },
          { question: "Quanto custa descupinizar em Copacabana?", answer: "A partir de R$350 para apartamentos. Depende da extensão. Solicite vistoria gratuita." },
          { question: "O vizinho pode ter cupins que migram para meu apartamento?", answer: "Sim. Cupins de madeira seca podem migrar por batentes compartilhados. Recomendamos tratar todos os pontos de madeira." },
          { question: "O tratamento tem cheiro forte?", answer: "Não. O gel cupinicida é inodoro e a aplicação é pontual, sem necessidade de evacuar o imóvel." },
        ],
        trustProof: "Experiência comprovada no tratamento de cupins em prédios de Copacabana.",
      },
    ],
  },

  /* 3 — IPANEMA */
  {
    slug: "ipanema",
    name: "Ipanema",
    h1: "Dedetizadora em Ipanema — Controle de Pragas para Residências e Comércios",
    metaTitle: "Dedetizadora em Ipanema RJ | Orçamento Grátis",
    metaDescription: "Dedetizadora em Ipanema com garantia de resultado. Atendemos apartamentos e comércios na região. Orçamento grátis: (21) 96850-0754.",
    keyword: "dedetizadora em Ipanema",
    context: "Ipanema combina prédios residenciais de alto padrão com uma cena gastronômica vibrante. A proximidade da Lagoa Rodrigo de Freitas e a vegetação da Praça Nossa Senhora da Paz favorecem mosquitos e baratas. Os prédios, muitos com décadas de construção, demandam manutenção preventiva contra pragas. Restaurantes e bares da região necessitam de controle periódico para atender normas sanitárias.",
    topServices: ["Dedetização residencial", "Desratização comercial", "Controle de mosquitos"],
    faq: [
      { question: "Vocês atendem apartamentos em Ipanema?", answer: "Sim. Atendemos desde studios até coberturas em Ipanema. Agendamento rápido pelo WhatsApp." },
      { question: "Ipanema tem problema com mosquitos?", answer: "Sim. A proximidade da Lagoa favorece mosquitos. Oferecemos controle com larvicida e nebulização." },
      { question: "Vocês atendem bares e restaurantes?", answer: "Sim. Controle integrado com certificação para a Vigilância Sanitária e planos mensais." },
      { question: "O serviço é seguro para pets?", answer: "Totalmente seguro. Produtos sem cheiro e de baixa toxicidade, aprovados pela ANVISA." },
    ],
    services: [
      {
        slug: "dedetizacao",
        keyword: "dedetização em Ipanema",
        h1: "Dedetização em Ipanema — Proteção Completa para Apartamentos e Comércios",
        metaTitle: "Dedetização em Ipanema RJ | Resultado Garantido",
        metaDescription: "Dedetização profissional em Ipanema. Eliminamos baratas e insetos de apartamentos e comércios. Garantia de resultado. Ligue: (21) 96850-0754.",
        localContext: "Apartamentos em Ipanema sofrem com baratas que migram pelas tubulações dos prédios e formigas atraídas por resíduos orgânicos. A proximidade de restaurantes na Rua Garcia D'Ávila e adjacências intensifica o problema nos andares baixos. Nosso tratamento inclui gel nos pontos críticos e barreira perimetral nas áreas comuns.",
        infestationSigns: [
          "Baratas aparecendo na cozinha e banheiro à noite",
          "Formigas em trilha constante na bancada",
          "Insetos atraídos pela iluminação do hall",
          "Pequenos insetos nos ralos do banheiro",
        ],
        serviceProcess: [
          "Inspeção técnica gratuita no apartamento em Ipanema",
          "Aplicação de gel em cozinha, banheiros e ralos",
          "Pulverização residual sem cheiro nos rodapés",
          "Garantia de 3 a 6 meses com revisão incluída",
        ],
        faq: [
          { question: "A dedetização em Ipanema precisa evacuar o imóvel?", answer: "Não. Gel e produtos sem cheiro permitem que você permaneça durante a aplicação." },
          { question: "Quanto tempo demora?", answer: "40 a 60 minutos para apartamentos de 2 quartos." },
          { question: "As baratas voltam depois?", answer: "O tratamento cria barreira residual de até 6 meses com revisão gratuita na garantia." },
          { question: "Vocês emitem certificado?", answer: "Sim. Certificado válido para condomínios e comércios." },
        ],
        trustProof: "Experiência consolidada com apartamentos e comércios em Ipanema.",
      },
      {
        slug: "desratizacao",
        keyword: "desratização em Ipanema",
        h1: "Desratização em Ipanema — Elimine Ratos de Prédios e Estabelecimentos",
        metaTitle: "Desratização em Ipanema RJ | Sem Ratos",
        metaDescription: "Desratização em Ipanema. Ratos em prédios, restaurantes e lixeiras. Métodos seguros. Orçamento grátis: (21) 96850-0754.",
        localContext: "As lixeiras coletivas dos prédios de Ipanema e os restaurantes da região atraem roedores. Ratos de esgoto transitam pelas galerias pluviais e acessam os subsolos. Nossa abordagem inclui porta-iscas nas áreas externas, vedação de acessos e orientação sobre armazenamento de resíduos.",
        infestationSigns: [
          "Fezes de roedores na área de serviço ou garagem",
          "Marcas de roedura em fios e embalagens",
          "Barulhos nas paredes durante a madrugada",
          "Ratos vistos próximo a lixeiras à noite",
        ],
        serviceProcess: [
          "Vistoria técnica das áreas comuns em Ipanema",
          "Instalação de porta-iscas seguros em pontos críticos",
          "Vedação de pontos de entrada no prédio",
          "Monitoramento quinzenal por 60 dias",
        ],
        faq: [
          { question: "Porta-iscas são seguros?", answer: "Sim. Com chave de segurança, inacessíveis para crianças e pets." },
          { question: "Quanto tempo para controlar?", answer: "15 a 30 dias para controle total com monitoramento contínuo." },
          { question: "Atendem restaurantes?", answer: "Sim. Planos mensais com certificado sanitário." },
          { question: "Ratos sobem nos andares?", answer: "Sim. Incluímos vedação de pontos de acesso em tubulações." },
        ],
        trustProof: "Experiência comprovada com prédios residenciais e comércios em Ipanema.",
      },
      {
        slug: "descupinizacao",
        keyword: "descupinização em Ipanema",
        h1: "Descupinização em Ipanema — Proteja Móveis e Estruturas de Madeira",
        metaTitle: "Descupinização em Ipanema | Cupins Nunca Mais",
        metaDescription: "Descupinização em Ipanema. Cupins em prédios e apartamentos. Garantia estendida. Ligue: (21) 96850-0754.",
        localContext: "Apartamentos de Ipanema com piso de taco e mobiliário nobre são alvo de cupins de madeira seca. A umidade marítima acelera a deterioração. Nosso tratamento preserva os acabamentos originais aplicando gel cupinicida nos pontos afetados sem necessidade de obra.",
        infestationSigns: [
          "Serragem fina em rodapés e batentes",
          "Orifícios em tacos de madeira do piso",
          "Asas de cupins após revoada noturna",
          "Madeira que soa oca",
        ],
        serviceProcess: [
          "Inspeção detalhada de pisos e madeiras",
          "Identificação da espécie de cupim",
          "Aplicação de gel cupinicida sem cheiro",
          "Garantia de 2 a 5 anos com acompanhamento",
        ],
        faq: [
          { question: "O gel danifica os tacos?", answer: "Não. A aplicação é pontual e preserva os acabamentos." },
          { question: "Quanto custa?", answer: "A partir de R$350. Solicite vistoria gratuita." },
          { question: "Preciso sair do apartamento?", answer: "Não para tratamento com gel. Sem cheiro e sem inconvenientes." },
          { question: "Tem garantia?", answer: "Sim. De 2 a 5 anos com vistorias periódicas incluídas." },
        ],
        trustProof: "Experiência com apartamentos de alto padrão em Ipanema.",
      },
    ],
  },

  /* 4 — LEBLON */
  {
    slug: "leblon",
    name: "Leblon",
    h1: "Dedetizadora no Leblon — Controle de Pragas para Apartamentos e Restaurantes",
    metaTitle: "Dedetizadora no Leblon RJ | Orçamento Grátis",
    metaDescription: "Dedetizadora no Leblon com garantia de resultado. Atendemos prédios e comércios. Orçamento grátis: (21) 96850-0754.",
    keyword: "dedetizadora no Leblon",
    context: "O Leblon é um dos bairros mais valorizados do Rio, com prédios residenciais de alto padrão e uma forte concentração gastronômica. A proximidade do Jardim de Alah e a Lagoa favorecem mosquitos e baratas. Os restaurantes e bares da Rua Dias Ferreira demandam controle rigoroso para atender normas da Vigilância Sanitária.",
    topServices: ["Dedetização residencial premium", "Desratização para comércios", "Descupinização de mobiliário"],
    faq: [
      { question: "Vocês atendem apartamentos no Leblon?", answer: "Sim. Atendemos todos os tipos de imóveis no Leblon, de studios a coberturas." },
      { question: "O serviço é discreto?", answer: "Sim. Equipe uniformizada e veículos sem identificação quando solicitado." },
      { question: "Atendem restaurantes na Dias Ferreira?", answer: "Sim. Planos mensais com certificação sanitária." },
      { question: "Produtos são seguros?", answer: "Todos aprovados pela ANVISA, sem cheiro e de baixa toxicidade." },
    ],
    services: [
      {
        slug: "dedetizacao",
        keyword: "dedetização no Leblon",
        h1: "Dedetização no Leblon — Serviço Premium para Apartamentos e Comércios",
        metaTitle: "Dedetização no Leblon RJ | Resultado Garantido",
        metaDescription: "Dedetização profissional no Leblon. Apartamentos e restaurantes. Produtos seguros. Garantia de resultado. Ligue: (21) 96850-0754.",
        localContext: "Apartamentos de alto padrão no Leblon exigem tratamento discreto e eficaz. Baratas e formigas migram pelas tubulações dos prédios, especialmente nos andares próximos a restaurantes. Nosso gel inodoro e a pulverização sem cheiro garantem conforto total durante e após o serviço.",
        infestationSigns: ["Baratas na cozinha à noite", "Formigas nos armários", "Insetos nos ralos do banheiro", "Baratas voando no hall de entrada"],
        serviceProcess: ["Inspeção técnica gratuita no Leblon", "Gel inodoro nos pontos críticos", "Pulverização residual sem cheiro", "Garantia de 3 a 6 meses"],
        faq: [
          { question: "A dedetização tem cheiro?", answer: "Não. Usamos produtos inodoros de última geração." },
          { question: "Quanto tempo demora?", answer: "40 a 60 minutos para apartamentos padrão." },
          { question: "Posso ficar no apartamento?", answer: "Sim. Sem necessidade de evacuação." },
          { question: "Vocês atendem coberturas?", answer: "Sim. Atendemos todos os formatos de imóvel no Leblon." },
        ],
        trustProof: "Atendimento premium em apartamentos de alto padrão no Leblon.",
      },
      {
        slug: "desratizacao",
        keyword: "desratização no Leblon",
        h1: "Desratização no Leblon — Elimine Ratos com Segurança e Discrição",
        metaTitle: "Desratização no Leblon RJ | Sem Ratos",
        metaDescription: "Desratização no Leblon. Ratos em prédios e restaurantes. Métodos seguros e discretos. Orçamento grátis: (21) 96850-0754.",
        localContext: "A concentração de restaurantes e bares no Leblon atrai roedores que transitam pelos esgotos e acessam subsolos. Nossa abordagem inclui porta-iscas discretos, vedação de acessos e monitoramento contínuo.",
        infestationSigns: ["Fezes de roedores em garagens", "Roedura em fios elétricos", "Ratos vistos perto de lixeiras", "Barulhos no forro à noite"],
        serviceProcess: ["Vistoria técnica no Leblon", "Porta-iscas seguros em pontos estratégicos", "Vedação de acessos", "Monitoramento por 60 dias"],
        faq: [
          { question: "O serviço é discreto?", answer: "Sim. Equipamento e equipe sem identificação quando solicitado." },
          { question: "Tempo para resultado?", answer: "15 a 30 dias para controle total." },
          { question: "Atendem restaurantes?", answer: "Sim. Planos mensais com certificação sanitária." },
          { question: "Seguro para pets?", answer: "Sim. Porta-iscas com chave de segurança." },
        ],
        trustProof: "Atendimento discreto e eficaz para prédios e comércios no Leblon.",
      },
      {
        slug: "descupinizacao",
        keyword: "descupinização no Leblon",
        h1: "Descupinização no Leblon — Proteja Mobiliário e Estruturas Nobres",
        metaTitle: "Descupinização no Leblon | Cupins Nunca Mais",
        metaDescription: "Descupinização no Leblon. Cupins em móveis e estruturas de madeira nobre. Garantia estendida. Ligue: (21) 96850-0754.",
        localContext: "O Leblon abriga apartamentos com acabamentos em madeira nobre — pisos, batentes e mobiliário de alto valor — que são alvo de cupins de madeira seca. A umidade marítima favorece infestações silenciosas que só são detectadas quando há dano visível. Nosso diagnóstico precoce e tratamento com gel preservam o patrimônio.",
        infestationSigns: ["Serragem fina junto a móveis", "Orifícios em batentes de madeira", "Madeira oca ao toque", "Revoada de cupins à noite"],
        serviceProcess: ["Inspeção de todas as madeiras", "Diagnóstico da espécie", "Gel cupinicida sem cheiro", "Garantia de 2 a 5 anos"],
        faq: [
          { question: "O tratamento danifica acabamentos?", answer: "Não. Gel é aplicado pontualmente sem marcas." },
          { question: "Quanto custa?", answer: "A partir de R$400. Vistoria gratuita." },
          { question: "Preciso sair?", answer: "Não. Tratamento com gel é inodoro." },
          { question: "Garantia?", answer: "2 a 5 anos com vistorias periódicas." },
        ],
        trustProof: "Proteção de mobiliário nobre em apartamentos do Leblon.",
      },
    ],
  },

  /* 5 — TIJUCA */
  {
    slug: "tijuca",
    name: "Tijuca",
    h1: "Dedetizadora na Tijuca — Controle de Pragas para Residências e Comércios",
    metaTitle: "Dedetizadora na Tijuca RJ | Orçamento Grátis",
    metaDescription: "Dedetizadora na Tijuca com garantia de resultado. Atendemos casas, prédios e comércios. Orçamento grátis: (21) 96850-0754.",
    keyword: "dedetizadora na Tijuca",
    context: "A Tijuca é um dos maiores bairros residenciais do Rio, com uma mistura de casas, prédios e comércios. A proximidade da Floresta da Tijuca traz insetos silvestres e facilita a presença de escorpiões e aranhas. As ruas com muitas árvores e a topografia com encostas criam microclimas úmidos favoráveis a cupins e baratas.",
    topServices: ["Dedetização residencial", "Controle de escorpiões", "Descupinização de casas"],
    faq: [
      { question: "Vocês atendem casas na Tijuca?", answer: "Sim. Casas, sobrados e prédios de todos os portes na Tijuca e arredores." },
      { question: "A Tijuca tem escorpiões?", answer: "Sim. A proximidade da floresta traz escorpiões. Oferecemos controle perimetral específico." },
      { question: "Atendem comércios na Rua Haddock Lobo?", answer: "Sim. Planos para lojas e restaurantes com certificação sanitária." },
      { question: "Qual a frequência ideal?", answer: "A cada 4-6 meses para residências. Mensal para comércios alimentícios." },
    ],
    services: [
      {
        slug: "dedetizacao",
        keyword: "dedetização na Tijuca",
        h1: "Dedetização na Tijuca — Proteção para Casas e Prédios Próximos à Floresta",
        metaTitle: "Dedetização na Tijuca RJ | Resultado Garantido",
        metaDescription: "Dedetização profissional na Tijuca. Casas e prédios próximos à floresta. Produtos seguros. Resultado garantido. Ligue: (21) 96850-0754.",
        localContext: "Casas e prédios na Tijuca sofrem com insetos que vêm da mata: baratas, formigas cortadeiras e escorpiões-amarelos. O microclima úmido das encostas potencializa a reprodução. Nosso tratamento inclui barreira perimetral externa e gel interno para proteção completa.",
        infestationSigns: ["Baratas vindo do quintal ou jardim", "Escorpiões na garagem ou quintal", "Formigas cortadeiras atacando plantas", "Insetos noturnos atraídos pela luz"],
        serviceProcess: ["Inspeção técnica em casa ou prédio na Tijuca", "Barreira perimetral externa contra insetos", "Gel e pulverização interna sem cheiro", "Garantia de 3 a 6 meses"],
        faq: [
          { question: "A proximidade da floresta piora as pragas?", answer: "Sim. Por isso o tratamento perimetral externo é essencial para casas na Tijuca." },
          { question: "O tratamento elimina escorpiões?", answer: "O controle perimetral reduz drasticamente. Também orientamos sobre vedação de ralos." },
          { question: "Quanto tempo demora?", answer: "1 a 2 horas para casas médias." },
          { question: "Posso ficar em casa?", answer: "Sim. Produtos seguros e sem cheiro." },
        ],
        trustProof: "Experiência com casas e prédios próximos à Floresta da Tijuca.",
      },
      {
        slug: "desratizacao",
        keyword: "desratização na Tijuca",
        h1: "Desratização na Tijuca — Controle de Ratos em Casas e Estabelecimentos",
        metaTitle: "Desratização na Tijuca RJ | Sem Ratos",
        metaDescription: "Desratização na Tijuca. Ratos em casas, prédios e comércios. Métodos seguros. Orçamento grátis: (21) 96850-0754.",
        localContext: "A Tijuca possui uma rede de esgoto extensa e ruas com muitas árvores frutíferas que atraem roedores. Casas com quintais e prédios antigos são os mais vulneráveis. Nossa abordagem inclui porta-iscas, vedação e orientação sobre manejo de resíduos.",
        infestationSigns: ["Fezes de roedores no quintal ou garagem", "Frutas mordidas em árvores do quintal", "Roedura em fios e caixas no depósito", "Ratos vistos nas calçadas à noite"],
        serviceProcess: ["Vistoria completa na propriedade na Tijuca", "Porta-iscas seguros em pontos estratégicos", "Vedação de buracos e frestas", "Monitoramento por 60 dias"],
        faq: [
          { question: "Árvores frutíferas atraem ratos?", answer: "Sim. Recomendamos poda e coleta frequente de frutos caídos, além do controle profissional." },
          { question: "Porta-iscas são seguros?", answer: "Sim. Com chave, inacessíveis para crianças e pets." },
          { question: "Quanto tempo para controlar?", answer: "15 a 30 dias com monitoramento contínuo." },
          { question: "Atendem comércios?", answer: "Sim. Planos mensais com certificado sanitário." },
        ],
        trustProof: "Experiência com desratização em casas e prédios da Tijuca.",
      },
      {
        slug: "descupinizacao",
        keyword: "descupinização na Tijuca",
        h1: "Descupinização na Tijuca — Proteja Casas e Estruturas de Madeira",
        metaTitle: "Descupinização na Tijuca | Cupins Nunca Mais",
        metaDescription: "Descupinização na Tijuca. Cupins em casas e prédios. Garantia estendida. Ligue: (21) 96850-0754.",
        localContext: "Casas antigas na Tijuca possuem estruturas em madeira — telhados, vigas e pisos — que são alvo de cupins subterrâneos e de madeira seca. A umidade das encostas acelera a deterioração. O tratamento adequado preserva o patrimônio e evita danos estruturais.",
        infestationSigns: ["Serragem em rodapés e vigas do telhado", "Túneis de barro subindo paredes", "Madeira comprometida no telhado", "Revoada de cupins no verão"],
        serviceProcess: ["Inspeção de telhado, pisos e estruturas", "Diagnóstico da espécie", "Calda cupinicida ou gel conforme necessidade", "Garantia de 2 a 5 anos"],
        faq: [
          { question: "Cupins atacam telhados na Tijuca?", answer: "Sim. Vigas de madeira são alvo frequente. Recomendamos inspeção preventiva anual." },
          { question: "Quanto custa?", answer: "A partir de R$400 para casas. Depende da extensão. Vistoria gratuita." },
          { question: "O tratamento é seguro?", answer: "Sim. Produtos aprovados pela ANVISA." },
          { question: "Garantia?", answer: "2 a 5 anos com vistorias periódicas." },
        ],
        trustProof: "Experiência com casas e estruturas de madeira na Tijuca.",
      },
    ],
  },

  /* 6 — BOTAFOGO */
  {
    slug: "botafogo",
    name: "Botafogo",
    h1: "Dedetizadora em Botafogo — Controle de Pragas em Prédios e Comércios",
    metaTitle: "Dedetizadora em Botafogo RJ | Orçamento Grátis",
    metaDescription: "Dedetizadora em Botafogo com garantia de resultado. Prédios e comércios. Orçamento grátis: (21) 96850-0754.",
    keyword: "dedetizadora em Botafogo",
    context: "Botafogo é um bairro dinâmico com prédios residenciais, escritórios e comércios. A proximidade da Enseada de Botafogo e as áreas verdes do bairro favorecem mosquitos e baratas. Os prédios com décadas de construção possuem infraestrutura que facilita a circulação de pragas entre unidades. A vida comercial intensa demanda controle periódico.",
    topServices: ["Dedetização de prédios", "Desratização comercial", "Controle de mosquitos"],
    faq: [
      { question: "Vocês atendem prédios em Botafogo?", answer: "Sim. Prédios residenciais e comerciais de todos os portes." },
      { question: "Botafogo tem problema com mosquitos?", answer: "Sim. A proximidade da enseada favorece. Oferecemos controle com nebulização." },
      { question: "Atendem escritórios?", answer: "Sim. Controle de pragas para empresas com certificação sanitária." },
      { question: "Quanto custa?", answer: "Orçamento sob medida. Solicite avaliação gratuita." },
    ],
    services: [
      {
        slug: "dedetizacao",
        keyword: "dedetização em Botafogo",
        h1: "Dedetização em Botafogo — Proteção para Prédios Residenciais e Comerciais",
        metaTitle: "Dedetização em Botafogo RJ | Resultado Garantido",
        metaDescription: "Dedetização profissional em Botafogo. Prédios e escritórios. Produtos seguros. Resultado garantido. Ligue: (21) 96850-0754.",
        localContext: "Prédios em Botafogo enfrentam baratas que circulam pelas galerias pluviais e formigas atraídas pela alta atividade comercial. Nosso tratamento combina gel interno com barreira perimetral nas áreas comuns para proteção completa.",
        infestationSigns: ["Baratas nas áreas comuns", "Formigas em escritórios", "Insetos nos ralos", "Mosquitos em excesso"],
        serviceProcess: ["Inspeção gratuita no prédio", "Gel nos pontos críticos", "Barreira perimetral", "Garantia de 3 a 6 meses"],
        faq: [
          { question: "Dedetização atrapalha o trabalho no escritório?", answer: "Não. Produtos sem cheiro e aplicação rápida." },
          { question: "Quanto tempo?", answer: "45 minutos por unidade em média." },
          { question: "Posso permanecer no local?", answer: "Sim. Sem necessidade de evacuação." },
          { question: "Certificado?", answer: "Sim. Para condomínios e empresas." },
        ],
        trustProof: "Experiência com prédios residenciais e comerciais em Botafogo.",
      },
      {
        slug: "desratizacao",
        keyword: "desratização em Botafogo",
        h1: "Desratização em Botafogo — Controle de Ratos em Prédios e Restaurantes",
        metaTitle: "Desratização em Botafogo RJ | Sem Ratos",
        metaDescription: "Desratização em Botafogo. Ratos em prédios e comércios. Métodos seguros. Orçamento grátis: (21) 96850-0754.",
        localContext: "Botafogo possui alta concentração de restaurantes e bares que geram resíduos orgânicos, atraindo roedores. Os prédios antigos com subsolos amplos oferecem abrigo. Nossa abordagem integra iscas seguras, vedação e orientação sobre gestão de resíduos.",
        infestationSigns: ["Fezes de roedores em garagens", "Marcas de roedura em fios", "Ratos vistos à noite", "Barulhos no forro"],
        serviceProcess: ["Vistoria técnica em Botafogo", "Porta-iscas seguros", "Vedação de acessos", "Monitoramento por 60 dias"],
        faq: [
          { question: "Atendem restaurantes?", answer: "Sim. Planos mensais com certificação sanitária." },
          { question: "Seguro para pets?", answer: "Sim. Porta-iscas com chave." },
          { question: "Quanto tempo?", answer: "15 a 30 dias para controle." },
          { question: "Custo?", answer: "Orçamento gratuito com vistoria." },
        ],
        trustProof: "Experiência com prédios e comércios em Botafogo.",
      },
      {
        slug: "descupinizacao",
        keyword: "descupinização em Botafogo",
        h1: "Descupinização em Botafogo — Proteja Estruturas e Mobiliário",
        metaTitle: "Descupinização em Botafogo | Cupins Nunca Mais",
        metaDescription: "Descupinização em Botafogo. Cupins em prédios e apartamentos. Garantia estendida. Ligue: (21) 96850-0754.",
        localContext: "Apartamentos em Botafogo com pisos de taco e esquadrias de madeira sofrem com cupins de madeira seca. A umidade do bairro litorâneo favorece colônias. Tratamento com gel preserva acabamentos sem necessidade de obra.",
        infestationSigns: ["Serragem em rodapés", "Orifícios em tacos", "Madeira oca", "Revoada de cupins"],
        serviceProcess: ["Inspeção de madeiras", "Diagnóstico da espécie", "Gel cupinicida", "Garantia de 2 a 5 anos"],
        faq: [
          { question: "Tacos de madeira são alvo?", answer: "Sim. Tratamento com gel preserva sem remoção." },
          { question: "Custo?", answer: "A partir de R$350. Vistoria gratuita." },
          { question: "Preciso sair?", answer: "Não. Gel inodoro." },
          { question: "Garantia?", answer: "2 a 5 anos." },
        ],
        trustProof: "Experiência com apartamentos e prédios em Botafogo.",
      },
    ],
  },

  /* 7 — FLAMENGO */
  {
    slug: "flamengo",
    name: "Flamengo",
    h1: "Dedetizadora no Flamengo — Controle de Pragas para Prédios e Residências",
    metaTitle: "Dedetizadora no Flamengo RJ | Orçamento Grátis",
    metaDescription: "Dedetizadora no Flamengo com garantia de resultado. Prédios e comércios. Orçamento grátis: (21) 96850-0754.",
    keyword: "dedetizadora no Flamengo",
    context: "O Flamengo é um bairro tradicional com prédios residenciais ao longo do Aterro e ruas arborizadas. A vegetação do Parque do Flamengo e a proximidade do mar criam ambiente propício para mosquitos, baratas e cupins. Os prédios das décadas de 40-70, com tubulações antigas, demandam atenção especial contra pragas.",
    topServices: ["Dedetização de prédios", "Controle de mosquitos", "Descupinização"],
    faq: [
      { question: "Vocês atendem prédios no Flamengo?", answer: "Sim. Prédios de todas as épocas." },
      { question: "O Aterro traz mais mosquitos?", answer: "Sim. Oferecemos controle especializado com nebulização." },
      { question: "Atendem comércios?", answer: "Sim. Com certificação sanitária." },
      { question: "Frequência ideal?", answer: "A cada 4-6 meses para residências." },
    ],
    services: [
      {
        slug: "dedetizacao",
        keyword: "dedetização no Flamengo",
        h1: "Dedetização no Flamengo — Proteção para Prédios Tradicionais e Modernos",
        metaTitle: "Dedetização no Flamengo RJ | Resultado Garantido",
        metaDescription: "Dedetização no Flamengo. Prédios e residências. Produtos seguros. Resultado garantido. Ligue: (21) 96850-0754.",
        localContext: "Prédios no Flamengo possuem tubulações compartilhadas que facilitam a migração de baratas entre andares. A vegetação do Aterro traz insetos noturnos. Nosso tratamento combina gel nas unidades e pulverização perimetral para proteção duradoura.",
        infestationSigns: ["Baratas à noite na cozinha", "Mosquitos vindos do Aterro", "Formigas na despensa", "Insetos nos ralos"],
        serviceProcess: ["Inspeção gratuita no Flamengo", "Gel em cozinhas e banheiros", "Pulverização perimetral", "Garantia de 3 a 6 meses"],
        faq: [
          { question: "Prédios antigos têm mais baratas?", answer: "Sim. Tubulações antigas facilitam. Nosso tratamento cria barreira residual." },
          { question: "Quanto tempo?", answer: "45 a 60 minutos por apartamento." },
          { question: "Seguro para pets?", answer: "Sim. Produtos inodoros e de baixa toxicidade." },
          { question: "Certificado?", answer: "Sim. Para condomínios e comércios." },
        ],
        trustProof: "Experiência com prédios tradicionais do Flamengo.",
      },
      {
        slug: "desratizacao",
        keyword: "desratização no Flamengo",
        h1: "Desratização no Flamengo — Controle de Ratos em Prédios e Comércios",
        metaTitle: "Desratização no Flamengo RJ | Sem Ratos",
        metaDescription: "Desratização no Flamengo. Ratos em prédios e comércios. Métodos seguros. Orçamento grátis: (21) 96850-0754.",
        localContext: "O Flamengo sofre com roedores que transitam pelas galerias de esgoto e acessam os prédios pelas lixeiras coletivas. Nosso serviço inclui porta-iscas, vedação e consultoria sobre armazenamento de resíduos.",
        infestationSigns: ["Fezes em garagens", "Roedura em fios", "Ratos perto de lixeiras", "Ruídos nas paredes"],
        serviceProcess: ["Diagnóstico no Flamengo", "Porta-iscas seguros", "Vedação de acessos", "Monitoramento por 60 dias"],
        faq: [
          { question: "Atendem prédios antigos?", answer: "Sim. Experiência com todas as tipologias." },
          { question: "Seguro?", answer: "Sim. Porta-iscas com chave." },
          { question: "Tempo?", answer: "15 a 30 dias para controle." },
          { question: "Custo?", answer: "Orçamento gratuito." },
        ],
        trustProof: "Experiência com prédios e comércios no Flamengo.",
      },
      {
        slug: "descupinizacao",
        keyword: "descupinização no Flamengo",
        h1: "Descupinização no Flamengo — Proteja Pisos e Estruturas Históricas",
        metaTitle: "Descupinização no Flamengo | Cupins Nunca Mais",
        metaDescription: "Descupinização no Flamengo. Cupins em prédios antigos. Garantia estendida. Ligue: (21) 96850-0754.",
        localContext: "Prédios históricos do Flamengo possuem pisos de taco, batentes e esquadrias em madeira nobre que são alvo de cupins de madeira seca. O tratamento com gel preserva os acabamentos originais sem intervenção invasiva.",
        infestationSigns: ["Serragem em rodapés e batentes", "Orifícios em tacos de madeira", "Madeira oca", "Asas de cupins após revoada"],
        serviceProcess: ["Inspeção de madeiras e estruturas", "Identificação da espécie", "Gel cupinicida inodoro", "Garantia de 2 a 5 anos"],
        faq: [
          { question: "Preserva acabamentos originais?", answer: "Sim. Gel aplicado pontualmente." },
          { question: "Custo?", answer: "A partir de R$350." },
          { question: "Preciso sair?", answer: "Não. Sem cheiro." },
          { question: "Garantia?", answer: "2 a 5 anos." },
        ],
        trustProof: "Proteção de prédios históricos no Flamengo.",
      },
    ],
  },

  /* 8 — CENTRO */
  {
    slug: "centro",
    name: "Centro",
    h1: "Dedetizadora no Centro do Rio — Controle de Pragas para Empresas e Comércios",
    metaTitle: "Dedetizadora no Centro RJ | Orçamento Grátis",
    metaDescription: "Dedetizadora no Centro do Rio de Janeiro. Empresas, restaurantes e comércios. Orçamento grátis: (21) 96850-0754.",
    keyword: "dedetizadora no Centro do Rio",
    context: "O Centro do Rio concentra escritórios, prédios comerciais, restaurantes e edifícios históricos. A alta circulação de pessoas e a presença de alimentação rápida geram resíduos que atraem pragas. Os prédios antigos com porões e subsolos são focos de roedores e baratas. O controle periódico é essencial para manter a conformidade sanitária.",
    topServices: ["Dedetização comercial", "Desratização de restaurantes", "Controle de pragas empresarial"],
    faq: [
      { question: "Vocês atendem empresas no Centro?", answer: "Sim. Escritórios, restaurantes e comércios de todos os portes." },
      { question: "Dedetização pode ser feita fora do horário comercial?", answer: "Sim. Oferecemos atendimento noturno e aos finais de semana." },
      { question: "Emitem certificado?", answer: "Sim. Certificação para a Vigilância Sanitária." },
      { question: "Qual a frequência para restaurantes?", answer: "Mensal, conforme exigência sanitária." },
    ],
    services: [
      {
        slug: "dedetizacao",
        keyword: "dedetização no Centro do Rio",
        h1: "Dedetização no Centro do Rio — Serviço para Empresas e Restaurantes",
        metaTitle: "Dedetização no Centro RJ | Resultado Garantido",
        metaDescription: "Dedetização no Centro do Rio. Empresas e restaurantes. Atendimento noturno. Resultado garantido. Ligue: (21) 96850-0754.",
        localContext: "Escritórios e restaurantes no Centro sofrem com baratas que migram pelo esgoto e formigas atraídas pelos resíduos de alimentação. Nosso atendimento pode ser realizado fora do horário comercial para não interromper as atividades. Tratamento com gel e barreira residual.",
        infestationSigns: ["Baratas nos banheiros do escritório", "Formigas na copa", "Insetos nos ralos", "Baratas na área de preparo de alimentos"],
        serviceProcess: ["Inspeção fora do horário comercial", "Gel nos pontos críticos", "Pulverização residual", "Garantia com certificado"],
        faq: [
          { question: "Atrapalha o expediente?", answer: "Não. Podemos atender à noite ou aos finais de semana." },
          { question: "Produtos seguros para alimentos?", answer: "Sim. Aprovados pela ANVISA para uso em áreas alimentícias." },
          { question: "Certificado?", answer: "Sim. Válido para Vigilância Sanitária." },
          { question: "Frequência?", answer: "Mensal para restaurantes, trimestral para escritórios." },
        ],
        trustProof: "Atendimento especializado para empresas do Centro do Rio.",
      },
      {
        slug: "desratizacao",
        keyword: "desratização no Centro do Rio",
        h1: "Desratização no Centro do Rio — Controle de Ratos em Prédios Comerciais",
        metaTitle: "Desratização no Centro RJ | Sem Ratos",
        metaDescription: "Desratização no Centro do Rio. Ratos em prédios e restaurantes. Atendimento noturno. Orçamento grátis: (21) 96850-0754.",
        localContext: "O Centro do Rio possui uma rede de esgoto antiga e grande volume de resíduos comerciais que alimentam populações de roedores. Prédios com subsolos e porões são especialmente vulneráveis. Nosso controle inclui iscas, vedação e manejo ambiental.",
        infestationSigns: ["Fezes em subsolos e depósitos", "Roedura em cabos e embalagens", "Ratos vistos nas calçadas", "Odor forte em ambientes fechados"],
        serviceProcess: ["Vistoria técnica no Centro", "Porta-iscas em pontos estratégicos", "Vedação de acessos", "Monitoramento por 60 dias"],
        faq: [
          { question: "O Centro tem mais ratos?", answer: "A alta concentração de resíduos comerciais favorece. Controle periódico é essencial." },
          { question: "Atendem à noite?", answer: "Sim. Para não interromper atividades comerciais." },
          { question: "Certificado?", answer: "Sim. Para a Vigilância Sanitária." },
          { question: "Custo?", answer: "Orçamento gratuito com vistoria." },
        ],
        trustProof: "Experiência com prédios comerciais e restaurantes no Centro do Rio.",
      },
      {
        slug: "descupinizacao",
        keyword: "descupinização no Centro do Rio",
        h1: "Descupinização no Centro do Rio — Proteja Prédios Históricos e Comerciais",
        metaTitle: "Descupinização no Centro RJ | Cupins Nunca Mais",
        metaDescription: "Descupinização no Centro do Rio. Cupins em prédios históricos e comerciais. Garantia estendida. Ligue: (21) 96850-0754.",
        localContext: "O Centro do Rio abriga prédios históricos com estruturas em madeira de lei que são patrimônio cultural. Cupins subterrâneos e de madeira seca ameaçam essas estruturas. Nosso tratamento especializado preserva o patrimônio com técnicas não invasivas.",
        infestationSigns: ["Serragem em estruturas de madeira", "Túneis de barro em paredes", "Madeira comprometida em telhados", "Revoada de cupins"],
        serviceProcess: ["Inspeção de estruturas históricas", "Diagnóstico especializado", "Tratamento não invasivo", "Garantia de 2 a 5 anos"],
        faq: [
          { question: "O tratamento danifica prédios históricos?", answer: "Não. Usamos técnicas não invasivas que preservam o patrimônio." },
          { question: "Custo?", answer: "Sob consulta. Depende da extensão e tipo de estrutura." },
          { question: "Atendem fora do horário?", answer: "Sim. Para não interromper atividades." },
          { question: "Garantia?", answer: "2 a 5 anos com vistorias." },
        ],
        trustProof: "Experiência com patrimônio histórico no Centro do Rio.",
      },
    ],
  },

  /* 9 — MADUREIRA */
  {
    slug: "madureira",
    name: "Madureira",
    h1: "Dedetizadora em Madureira — Controle de Pragas para Casas e Comércios",
    metaTitle: "Dedetizadora em Madureira RJ | Orçamento Grátis",
    metaDescription: "Dedetizadora em Madureira com garantia de resultado. Casas e comércios na região. Orçamento grátis: (21) 96850-0754.",
    keyword: "dedetizadora em Madureira",
    context: "Madureira é um dos maiores centros comerciais da Zona Norte do Rio, com mercados populares, lojas e residências. A alta atividade comercial e os mercados a céu aberto geram resíduos que atraem baratas, ratos e formigas. As casas e sobrados da região, muitos com quintais, demandam controle periódico contra pragas urbanas.",
    topServices: ["Dedetização residencial e comercial", "Desratização de comércios", "Controle de baratas"],
    faq: [
      { question: "Vocês atendem casas em Madureira?", answer: "Sim. Casas, sobrados e comércios de todos os portes." },
      { question: "Mercados atraem mais pragas?", answer: "Sim. A proximidade de mercados favorece. Oferecemos controle periódico." },
      { question: "Atendem lojas?", answer: "Sim. Controle de pragas com certificação sanitária." },
      { question: "Quanto custa?", answer: "A partir de R$150 para residências. Solicite orçamento." },
    ],
    services: [
      {
        slug: "dedetizacao",
        keyword: "dedetização em Madureira",
        h1: "Dedetização em Madureira — Proteção para Casas e Estabelecimentos Comerciais",
        metaTitle: "Dedetização em Madureira RJ | Resultado Garantido",
        metaDescription: "Dedetização profissional em Madureira. Casas e comércios. Produtos seguros. Resultado garantido. Ligue: (21) 96850-0754.",
        localContext: "Casas e comércios em Madureira sofrem com baratas e formigas atraídas pela intensa atividade comercial do bairro. Os mercados populares e a circulação de alimentos favorecem infestações recorrentes. Nosso tratamento inclui barreira perimetral externa e gel interno.",
        infestationSigns: ["Baratas na cozinha e quintal", "Formigas em trilha na despensa", "Insetos vindos do comércio vizinho", "Mosquitos no quintal"],
        serviceProcess: ["Inspeção gratuita em Madureira", "Barreira perimetral externa", "Gel interno em pontos críticos", "Garantia de 3 a 6 meses"],
        faq: [
          { question: "A proximidade do comércio piora?", answer: "Sim. Barreira perimetral é essencial para casas próximas." },
          { question: "Quanto tempo?", answer: "1 a 2 horas para casas." },
          { question: "Seguro para crianças?", answer: "Sim. Produtos aprovados pela ANVISA." },
          { question: "Certificado?", answer: "Sim. Para residências e comércios." },
        ],
        trustProof: "Equipe local em Madureira com atendimento rápido.",
      },
      {
        slug: "desratizacao",
        keyword: "desratização em Madureira",
        h1: "Desratização em Madureira — Controle de Ratos em Casas e Lojas",
        metaTitle: "Desratização em Madureira RJ | Sem Ratos",
        metaDescription: "Desratização em Madureira. Ratos em casas e comércios. Métodos seguros. Orçamento grátis: (21) 96850-0754.",
        localContext: "A intensa atividade comercial de Madureira gera grande volume de resíduos que alimentam populações de roedores. Casas com quintais e depósitos de lojas são os locais mais vulneráveis. Nossa abordagem combina iscas seguras, vedação e orientação sobre manejo de resíduos.",
        infestationSigns: ["Fezes de roedores no quintal ou depósito", "Marcas de roedura em embalagens", "Ratos vistos nas ruas à noite", "Barulhos no forro"],
        serviceProcess: ["Vistoria na propriedade em Madureira", "Porta-iscas seguros em pontos estratégicos", "Vedação de buracos e frestas", "Monitoramento por 60 dias"],
        faq: [
          { question: "Comércios atraem mais ratos?", answer: "Sim. Resíduos orgânicos são o principal atrativo. Controle periódico é fundamental." },
          { question: "Seguro para pets?", answer: "Sim. Porta-iscas com chave de segurança." },
          { question: "Tempo para resultado?", answer: "15 a 30 dias para controle." },
          { question: "Custo?", answer: "Orçamento gratuito." },
        ],
        trustProof: "Equipe baseada em Madureira com atendimento imediato.",
      },
      {
        slug: "descupinizacao",
        keyword: "descupinização em Madureira",
        h1: "Descupinização em Madureira — Proteja Casas e Estruturas de Madeira",
        metaTitle: "Descupinização em Madureira | Cupins Nunca Mais",
        metaDescription: "Descupinização em Madureira. Cupins em casas e lojas. Garantia estendida. Ligue: (21) 96850-0754.",
        localContext: "Casas em Madureira possuem telhados de madeira, portões e estruturas que são alvo de cupins. Os quintais com vegetação favorecem cupins subterrâneos. Nosso tratamento abrange eliminação de cupinzeiros, barreira no solo e tratamento de madeiras.",
        infestationSigns: ["Serragem em vigas do telhado", "Cupinzeiros no quintal", "Portões de madeira deteriorados", "Túneis de barro em paredes"],
        serviceProcess: ["Inspeção de telhado e estruturas", "Eliminação de cupinzeiros", "Barreira química no solo", "Garantia de 2 a 5 anos"],
        faq: [
          { question: "Cupinzeiros no quintal atacam a casa?", answer: "Sim. As forrageiras buscam madeira nas construções. Eliminação do cupinzeiro é essencial." },
          { question: "Custo?", answer: "A partir de R$350. Vistoria gratuita." },
          { question: "Tratam telhados?", answer: "Sim. Vigas e estruturas de madeira." },
          { question: "Garantia?", answer: "2 a 5 anos." },
        ],
        trustProof: "Equipe local em Madureira para atendimento rápido.",
      },
    ],
  },

  /* 10 — JACAREPAGUÁ */
  {
    slug: "jacarepagua",
    name: "Jacarepaguá",
    h1: "Dedetizadora em Jacarepaguá — Controle de Pragas para Casas e Condomínios",
    metaTitle: "Dedetizadora em Jacarepaguá RJ | Orçamento Grátis",
    metaDescription: "Dedetizadora em Jacarepaguá com garantia de resultado. Casas, condomínios e comércios. Orçamento grátis: (21) 96850-0754.",
    keyword: "dedetizadora em Jacarepaguá",
    context: "Jacarepaguá é uma região extensa que mescla condomínios fechados, casas e áreas de mata atlântica. A proximidade de lagoas e áreas verdes favorece mosquitos, cupins e roedores. Os condomínios horizontais com jardins e piscinas demandam controle especializado. A região de Taquara e Pechincha, mais urbanizada, enfrenta problemas típicos de áreas comerciais.",
    topServices: ["Dedetização de condomínios", "Controle de mosquitos", "Desratização de casas"],
    faq: [
      { question: "Vocês atendem condomínios em Jacarepaguá?", answer: "Sim. Condomínios horizontais e verticais de todos os portes." },
      { question: "Jacarepaguá tem mais mosquitos?", answer: "Sim. Lagoas e áreas verdes favorecem. Oferecemos controle com nebulização e larvicida." },
      { question: "Atendem a região da Taquara?", answer: "Sim. Toda a extensão de Jacarepaguá." },
      { question: "Quanto custa?", answer: "Orçamento sob medida. Solicite avaliação gratuita." },
    ],
    services: [
      {
        slug: "dedetizacao",
        keyword: "dedetização em Jacarepaguá",
        h1: "Dedetização em Jacarepaguá — Proteção para Condomínios e Casas",
        metaTitle: "Dedetização em Jacarepaguá RJ | Resultado Garantido",
        metaDescription: "Dedetização em Jacarepaguá. Condomínios e casas. Produtos seguros. Resultado garantido. Ligue: (21) 96850-0754.",
        localContext: "Casas e condomínios em Jacarepaguá sofrem com insetos vindos das áreas de mata e lagoas. Baratas, mosquitos e formigas são os mais frequentes. Nosso tratamento inclui barreira perimetral nas áreas externas e gel nos interiores para proteção completa.",
        infestationSigns: ["Baratas vindas do jardim", "Mosquitos em excesso perto de lagoas", "Formigas cortadeiras no jardim", "Insetos noturnos atraídos pela luz"],
        serviceProcess: ["Inspeção gratuita em Jacarepaguá", "Barreira perimetral externa", "Gel interno em pontos críticos", "Garantia de 3 a 6 meses"],
        faq: [
          { question: "Áreas de mata trazem mais pragas?", answer: "Sim. Barreira perimetral é essencial para casas próximas." },
          { question: "Quanto tempo?", answer: "1 a 2 horas para casas e condomínios." },
          { question: "Seguro para crianças e pets?", answer: "Sim. Produtos aprovados pela ANVISA." },
          { question: "Certificado?", answer: "Sim. Para condomínios e comércios." },
        ],
        trustProof: "Experiência com condomínios e casas em Jacarepaguá.",
      },
      {
        slug: "desratizacao",
        keyword: "desratização em Jacarepaguá",
        h1: "Desratização em Jacarepaguá — Controle de Ratos em Casas e Condomínios",
        metaTitle: "Desratização em Jacarepaguá RJ | Sem Ratos",
        metaDescription: "Desratização em Jacarepaguá. Ratos em casas e condomínios. Métodos seguros. Orçamento grátis: (21) 96850-0754.",
        localContext: "Jacarepaguá possui áreas de mata e lagoas que abrigam roedores silvestres. Condomínios com jardins e casas com quintais são os mais afetados. Nossa abordagem integra iscas seguras, vedação e manejo ambiental.",
        infestationSigns: ["Fezes no quintal ou garagem", "Frutas roídas em árvores", "Roedura em fios", "Ratos vistos no jardim"],
        serviceProcess: ["Vistoria na propriedade em Jacarepaguá", "Porta-iscas seguros", "Vedação de acessos", "Monitoramento por 60 dias"],
        faq: [
          { question: "Mato atrai mais ratos?", answer: "Sim. Áreas verdes abrigam roedores que buscam alimento nas casas." },
          { question: "Seguro para pets?", answer: "Sim. Porta-iscas com chave." },
          { question: "Tempo?", answer: "15 a 30 dias." },
          { question: "Custo?", answer: "Orçamento gratuito." },
        ],
        trustProof: "Experiência com casas e condomínios em Jacarepaguá.",
      },
      {
        slug: "descupinizacao",
        keyword: "descupinização em Jacarepaguá",
        h1: "Descupinização em Jacarepaguá — Proteja Casas e Estruturas de Madeira",
        metaTitle: "Descupinização em Jacarepaguá | Cupins Nunca Mais",
        metaDescription: "Descupinização em Jacarepaguá. Cupins em casas e condomínios. Garantia estendida. Ligue: (21) 96850-0754.",
        localContext: "Casas em Jacarepaguá com decks, pergolados e telhados de madeira são alvo de cupins subterrâneos vindos das áreas de mata. A umidade tropical favorece colônias grandes. Nosso tratamento inclui eliminação de cupinzeiros e barreira no solo.",
        infestationSigns: ["Cupinzeiros no terreno", "Serragem em decks e pergolados", "Túneis de barro em muros", "Madeira do telhado comprometida"],
        serviceProcess: ["Inspeção de todas as estruturas", "Eliminação de cupinzeiros", "Barreira química perimetral", "Garantia de 2 a 5 anos"],
        faq: [
          { question: "Cupins vêm da mata?", answer: "Sim. Cupins subterrâneos migram de áreas verdes para construções." },
          { question: "Custo?", answer: "A partir de R$400. Vistoria gratuita." },
          { question: "Tratam pergolados e decks?", answer: "Sim. Madeiras externas são alvo frequente." },
          { question: "Garantia?", answer: "2 a 5 anos com vistorias." },
        ],
        trustProof: "Experiência com casas e condomínios em Jacarepaguá.",
      },
    ],
  },
];

/** Helper: find bairro by slug */
export function getBairroRioBySlug(slug: string): Bairro | undefined {
  return bairrosRio.find((b) => b.slug === slug);
}

/* ─── Service Templates for Rio (with Rio phone number) ─── */
interface ServiceTemplateRio {
  label: string;
  generateService: (bairroName: string) => BairroService;
}

const SERVICE_TEMPLATES_RIO: Record<string, ServiceTemplateRio> = {
  formigas: {
    label: "Controle de Formigas",
    generateService: (name) => ({
      slug: "formigas",
      keyword: `controle de formigas em ${name}`,
      h1: `Controle de Formigas em ${name} — Elimine Colônias com Tratamento Profissional`,
      metaTitle: `Controle de Formigas em ${name} RJ | Orçamento Grátis | USA`,
      metaDescription: `Controle de formigas em ${name}. Eliminamos colônias com gel e iscas profissionais. Mais de 20 anos de experiência. Orçamento grátis: (21) 96850-0754.`,
      localContext: `Formigas são uma das pragas mais comuns em ${name}, invadindo cozinhas, despensas e áreas de alimentação. No Rio de Janeiro, o clima quente e úmido favorece colônias durante o ano inteiro. Nossas técnicas incluem gel atrativo, iscas granuladas e pulverização direcionada para eliminar a colônia na origem. Tratamento seguro para crianças e pets.`,
      infestationSigns: [
        "Trilhas de formigas na cozinha ou bancadas",
        "Formigas dentro de armários e despensas",
        "Pequenos montículos de terra em frestas",
        "Formigas em fila carregando alimentos",
      ],
      serviceProcess: [
        `Identificação da espécie e localização dos ninhos em ${name}`,
        "Aplicação de gel atrativo e iscas profissionais",
        "Tratamento de frestas e pontos de entrada",
        "Orientação preventiva e garantia do serviço",
      ],
      faq: [
        { question: `Vocês eliminam formigas em ${name}?`, answer: "Sim. Utilizamos gel atrativo e iscas que eliminam a colônia inteira. Tratamento seguro e eficaz." },
        { question: "Quanto tempo para as formigas sumirem?", answer: "Em 3 a 7 dias a colônia é eliminada. O gel é levado pelas operárias até a rainha." },
        { question: "É seguro para pets?", answer: "Sim. Usamos produtos específicos em gel e iscas confinadas, sem risco para animais domésticos." },
        { question: "Qual o custo do serviço?", answer: "Varia conforme o tamanho do imóvel. Solicite orçamento grátis pelo WhatsApp: (21) 96850-0754." },
      ],
      trustProof: `Mais de 20 anos eliminando formigas em ${name} e região do Rio.`,
    }),
  },
  sanitizacao: {
    label: "Sanitização",
    generateService: (name) => ({
      slug: "sanitizacao",
      keyword: `sanitização em ${name}`,
      h1: `Sanitização em ${name} — Ambientes Limpos e Desinfectados`,
      metaTitle: `Sanitização em ${name} RJ | Desinfecção Profissional | USA`,
      metaDescription: `Sanitização profissional em ${name}. Desinfecção de ambientes com produtos aprovados pela ANVISA. Ideal para pós-obra e empresas. (21) 96850-0754.`,
      localContext: `A sanitização em ${name} é essencial para residências pós-obra, escritórios, clínicas e estabelecimentos comerciais no Rio de Janeiro. Nosso processo utiliza desinfetantes hospitalares e nebulização UBV para eliminar vírus, bactérias e fungos de superfícies e do ar.`,
      infestationSigns: [
        "Odores persistentes no ambiente",
        "Mofo ou bolor em paredes e tetos",
        "Ambientes fechados por longos períodos",
        "Pós-obra com poeira e resíduos",
      ],
      serviceProcess: [
        `Avaliação do ambiente em ${name}`,
        "Aplicação de desinfetante hospitalar por nebulização",
        "Tratamento de superfícies de contato frequente",
        "Liberação do ambiente com laudo do serviço",
      ],
      faq: [
        { question: `Vocês fazem sanitização em ${name}?`, answer: "Sim. Atendemos residências, escritórios, clínicas e comércios com produtos aprovados pela ANVISA." },
        { question: "Quanto tempo leva a sanitização?", answer: "Em média 1 a 2 horas. O local pode ser utilizado após algumas horas." },
        { question: "A sanitização elimina vírus?", answer: "Sim. Nossos produtos são eficazes contra vírus, bactérias e fungos." },
        { question: "Qual o custo?", answer: "Depende da metragem. Solicite orçamento grátis: (21) 96850-0754." },
      ],
      trustProof: `Sanitização profissional em ${name} com produtos hospitalares.`,
    }),
  },
  "anti-tracas": {
    label: "Controle de Traças",
    generateService: (name) => ({
      slug: "anti-tracas",
      keyword: `controle de traças em ${name}`,
      h1: `Controle de Traças em ${name} — Proteja Roupas, Livros e Documentos`,
      metaTitle: `Controle de Traças em ${name} RJ | Proteção | USA`,
      metaDescription: `Controle de traças em ${name}. Proteja roupas, livros e documentos. Tratamento profissional com garantia. Orçamento grátis: (21) 96850-0754.`,
      localContext: `Traças causam danos silenciosos em ${name}, destruindo roupas, livros e documentos. A umidade do litoral carioca favorece a proliferação desses insetos em armários e estantes. Nosso tratamento inclui pulverização residual, eliminando ovos e adultos com garantia.`,
      infestationSigns: [
        "Furos pequenos em roupas guardadas",
        "Páginas de livros com bordas corroídas",
        "Pequenos insetos prateados em gavetas",
        "Casulos ou teias finas em cantos de armários",
      ],
      serviceProcess: [
        `Inspeção de armários e estantes em ${name}`,
        "Aplicação de inseticida residual em pontos críticos",
        "Tratamento de frestas e gavetas infestadas",
        "Orientação de armazenamento preventivo",
      ],
      faq: [
        { question: `Vocês eliminam traças em ${name}?`, answer: "Sim. Nosso tratamento elimina traças adultas, larvas e ovos com produtos residuais de longa duração." },
        { question: "O produto mancha roupas?", answer: "Não. Utilizamos produtos específicos que não mancham tecidos nem danificam papéis." },
        { question: "Quanto tempo dura a proteção?", answer: "O efeito residual protege por até 6 meses. Recomendamos reforço semestral." },
        { question: "Qual o custo?", answer: "Varia conforme a quantidade de móveis. Solicite orçamento: (21) 96850-0754." },
      ],
      trustProof: `Proteção contra traças em ${name} com garantia de resultado.`,
    }),
  },
  "caixa-gordura": {
    label: "Limpeza de Caixa de Gordura",
    generateService: (name) => ({
      slug: "caixa-gordura",
      keyword: `limpeza de caixa de gordura em ${name}`,
      h1: `Limpeza de Caixa de Gordura em ${name} — Evite Entupimentos e Mau Cheiro`,
      metaTitle: `Limpeza Caixa de Gordura ${name} RJ | USA`,
      metaDescription: `Limpeza de caixa de gordura em ${name}. Evite entupimentos e mau cheiro. Atendimento rápido com garantia. Orçamento grátis: (21) 96850-0754.`,
      localContext: `A caixa de gordura requer limpeza periódica em ${name} para evitar entupimentos, refluxo de esgoto e mau cheiro. Restaurantes, padarias e residências precisam de manutenção regular. Nosso serviço inclui remoção completa da gordura, lavagem e destinação correta dos resíduos.`,
      infestationSigns: [
        "Mau cheiro vindo dos ralos da cozinha",
        "Pia da cozinha escoando devagar",
        "Refluxo de água nos ralos",
        "Gordura acumulada visível na caixa",
      ],
      serviceProcess: [
        `Abertura e inspeção da caixa de gordura em ${name}`,
        "Remoção mecânica de toda a gordura acumulada",
        "Lavagem e higienização completa",
        "Destinação correta dos resíduos",
      ],
      faq: [
        { question: `Vocês limpam caixa de gordura em ${name}?`, answer: "Sim. Atendemos residências, restaurantes e condomínios com serviço completo." },
        { question: "Com que frequência devo limpar?", answer: "A cada 3 a 6 meses para residências e mensalmente para restaurantes." },
        { question: "Vocês emitem comprovante?", answer: "Sim. Emitimos documentação do serviço para fins de fiscalização." },
        { question: "Qual o custo?", answer: "Depende do tamanho da caixa. Orçamento grátis: (21) 96850-0754." },
      ],
      trustProof: `Limpeza de caixa de gordura em ${name} com destinação correta.`,
    }),
  },
  "pulgas-carrapatos": {
    label: "Controle de Pulgas e Carrapatos",
    generateService: (name) => ({
      slug: "pulgas-carrapatos",
      keyword: `controle de pulgas e carrapatos em ${name}`,
      h1: `Controle de Pulgas e Carrapatos em ${name} — Proteção Para Sua Família e Pets`,
      metaTitle: `Pulgas e Carrapatos ${name} RJ | Controle | USA`,
      metaDescription: `Controle de pulgas e carrapatos em ${name}. Proteção para família e pets. Produtos seguros com garantia. Orçamento grátis: (21) 96850-0754.`,
      localContext: `Pulgas e carrapatos são frequentes em ${name}, especialmente em casas com animais e jardins. O clima quente do Rio de Janeiro favorece a reprodução o ano inteiro. Nosso tratamento combina pulverização do ambiente com produtos de efeito residual, eliminando ovos, larvas e adultos.`,
      infestationSigns: [
        "Pets se coçando excessivamente",
        "Picadas em tornozelos e pernas",
        "Pontos escuros (fezes de pulgas) em camas de pets",
        "Carrapatos visíveis em muros e jardins",
      ],
      serviceProcess: [
        `Inspeção do ambiente interno e externo em ${name}`,
        "Pulverização residual em pisos, tapetes e jardins",
        "Tratamento de áreas de descanso dos pets",
        "Orientação para tratamento conjunto nos animais",
      ],
      faq: [
        { question: `Vocês eliminam pulgas e carrapatos em ${name}?`, answer: "Sim. Tratamos o ambiente completo com produtos de efeito residual que eliminam todas as fases do ciclo." },
        { question: "Preciso tratar o pet também?", answer: "Sim. Recomendamos tratamento veterinário no pet em paralelo ao tratamento ambiental." },
        { question: "É seguro para crianças?", answer: "Sim. Produtos aprovados pela ANVISA com baixa toxicidade após secagem." },
        { question: "Quanto custa?", answer: "Depende da área. Orçamento grátis: (21) 96850-0754." },
      ],
      trustProof: `Controle de pulgas e carrapatos em ${name} com garantia.`,
    }),
  },
  "aranhas-escorpioes": {
    label: "Controle de Aranhas e Escorpiões",
    generateService: (name) => ({
      slug: "aranhas-escorpioes",
      keyword: `controle de aranhas e escorpiões em ${name}`,
      h1: `Controle de Aranhas e Escorpiões em ${name} — Proteção Para Sua Família`,
      metaTitle: `Aranhas e Escorpiões ${name} RJ | Proteção | USA`,
      metaDescription: `Controle de aranhas e escorpiões em ${name}. Proteção com equipe treinada. Mais de 20 anos. Orçamento grátis: (21) 96850-0754.`,
      localContext: `${name} registra ocorrências de escorpiões e aranhas, especialmente durante o verão carioca. Esses aracnídeos se escondem em entulhos, frestas e ralos. Nosso serviço inclui pulverização residual perimetral, vedação de frestas e tratamento de pontos de abrigo.`,
      infestationSigns: [
        "Escorpiões encontrados dentro de casa ou garagem",
        "Teias de aranha em cantos, forros e garagens",
        "Baratas frequentes (alimento de escorpiões)",
        "Entulho ou material acumulado próximo ao imóvel",
      ],
      serviceProcess: [
        `Inspeção de frestas, ralos e jardins em ${name}`,
        "Pulverização residual perimetral e interna",
        "Vedação de pontos de entrada identificados",
        "Monitoramento e garantia contratual",
      ],
      faq: [
        { question: `Tem escorpião em ${name}?`, answer: "Sim. Nosso tratamento cria uma barreira que impede a entrada no imóvel." },
        { question: "O tratamento é seguro para crianças?", answer: "Sim. Produtos aprovados pela ANVISA, seguros após secagem." },
        { question: "Quanto tempo dura a proteção?", answer: "O efeito residual dura de 3 a 6 meses. Oferecemos planos de manutenção." },
        { question: "Qual o custo?", answer: "Varia conforme o imóvel. Orçamento grátis: (21) 96850-0754." },
      ],
      trustProof: `Controle de aranhas e escorpiões em ${name} com garantia.`,
    }),
  },
  "afastamento-pombos": {
    label: "Afastamento de Pombos",
    generateService: (name) => ({
      slug: "afastamento-pombos",
      keyword: `afastamento de pombos em ${name}`,
      h1: `Afastamento de Pombos em ${name} — Solução Profissional e Humanitária`,
      metaTitle: `Afastamento de Pombos ${name} RJ | Humanitário | USA`,
      metaDescription: `Afastamento de pombos em ${name}. Espículas, telas e repelentes. Solução humanitária com garantia. Orçamento grátis: (21) 96850-0754.`,
      localContext: `Pombos causam problemas de saúde e danos estruturais em ${name}. Suas fezes são ácidas e danificam fachadas de prédios, além de transmitir doenças. Nosso serviço utiliza espículas, telas e gel repelente para afastar os pombos de forma humanitária.`,
      infestationSigns: [
        "Fezes de pombos em sacadas e fachadas",
        "Pombos nidificando em telhados ou ar-condicionado",
        "Penas acumuladas em áreas comuns",
        "Mau cheiro causado por fezes acumuladas",
      ],
      serviceProcess: [
        `Inspeção dos pontos de pouso em ${name}`,
        "Instalação de espículas e/ou telas de proteção",
        "Aplicação de gel repelente em superfícies",
        "Limpeza e higienização da área afetada",
      ],
      faq: [
        { question: `Vocês afastam pombos em ${name}?`, answer: "Sim. Métodos humanitários como espículas, telas e gel repelente que afastam sem prejudicar as aves." },
        { question: "O serviço é definitivo?", answer: "Sim, com barreiras físicas. Espículas e telas impedem o retorno." },
        { question: "Vocês limpam as fezes?", answer: "Sim. O serviço inclui higienização com desinfetante apropriado." },
        { question: "Qual o custo?", answer: "Depende da área. Orçamento grátis: (21) 96850-0754." },
      ],
      trustProof: `Afastamento humanitário de pombos em ${name} com garantia.`,
    }),
  },
  "limpeza-caixa-dagua": {
    label: "Limpeza de Caixa d'Água",
    generateService: (name) => ({
      slug: "limpeza-caixa-dagua",
      keyword: `limpeza de caixa d'água em ${name}`,
      h1: `Limpeza de Caixa d'Água em ${name} — Água Limpa e Segura`,
      metaTitle: `Limpeza Caixa d'Água ${name} RJ | Certificado | USA`,
      metaDescription: `Limpeza de caixa d'água em ${name}. Higienização completa com certificado. Casas e condomínios. Orçamento grátis: (21) 96850-0754.`,
      localContext: `A limpeza semestral da caixa d'água é recomendada pela ANVISA e essencial em ${name} para garantir água potável livre de bactérias, algas e sedimentos. Nosso serviço inclui esvaziamento, escovação, desinfecção e emissão de certificado. Atendemos casas e condomínios no Rio.`,
      infestationSigns: [
        "Água com cor amarelada ou turva",
        "Gosto ou cheiro estranho na água",
        "Mais de 6 meses sem limpeza",
        "Presença de sedimentos ou lodo no fundo",
      ],
      serviceProcess: [
        "Esvaziamento controlado da caixa d'água",
        "Escovação e remoção de lodo e sedimentos",
        "Desinfecção com hipoclorito de sódio",
        "Emissão de certificado de limpeza",
      ],
      faq: [
        { question: `Vocês limpam caixa d'água em ${name}?`, answer: "Sim. Atendemos casas, apartamentos e condomínios com certificado." },
        { question: "Com que frequência devo limpar?", answer: "A ANVISA recomenda a cada 6 meses ou sempre que houver alteração na água." },
        { question: "Vocês emitem certificado?", answer: "Sim. Certificado de limpeza para documentação e fiscalização." },
        { question: "Qual o custo?", answer: "Depende do volume. Orçamento grátis: (21) 96850-0754." },
      ],
      trustProof: `Limpeza de caixa d'água em ${name} com certificado.`,
    }),
  },
  fog: {
    label: "Sanitização FOG",
    generateService: (name) => ({
      slug: "fog",
      keyword: `sanitização FOG em ${name}`,
      h1: `Sanitização FOG em ${name} — Nebulização Industrial Profissional`,
      metaTitle: `Sanitização FOG ${name} RJ | Nebulização | USA`,
      metaDescription: `Sanitização FOG em ${name}. Nebulização industrial para galpões e grandes áreas. Mais de 20 anos de experiência. (21) 96850-0754.`,
      localContext: `A sanitização FOG é ideal para grandes áreas em ${name} como galpões, fábricas e depósitos. A nebulização a frio distribui microgotículas de forma uniforme, alcançando frestas e áreas de difícil acesso. Solução eficaz para grandes espaços no Rio de Janeiro.`,
      infestationSigns: [
        "Infestação em grandes áreas abertas ou galpões",
        "Mosquitos em áreas externas e jardins",
        "Necessidade de desinfecção de grande escala",
        "Pragas em depósitos e áreas industriais",
      ],
      serviceProcess: [
        `Avaliação da área em ${name}`,
        "Nebulização a frio com equipamento profissional",
        "Cobertura uniforme de toda a área tratada",
        "Documentação do serviço e orientações",
      ],
      faq: [
        { question: `Vocês fazem sanitização FOG em ${name}?`, answer: "Sim. Atendemos galpões, fábricas e grandes áreas com equipamento profissional." },
        { question: "É diferente da sanitização comum?", answer: "Sim. O FOG produz microgotículas que alcançam áreas inacessíveis, ideal para grandes espaços." },
        { question: "Quanto tempo leva?", answer: "Depende da área. Galpões médios levam de 2 a 4 horas." },
        { question: "Qual o custo?", answer: "Depende da metragem. Orçamento grátis: (21) 96850-0754." },
      ],
      trustProof: `Sanitização FOG profissional em ${name} e região.`,
    }),
  },
};

/** Generate a BairroService from Rio template */
function generateRioServiceFromTemplate(bairroName: string, serviceSlug: string): BairroService | undefined {
  const template = SERVICE_TEMPLATES_RIO[serviceSlug];
  if (!template) return undefined;
  return template.generateService(bairroName);
}

/** Helper: find bairro service (explicit data first, then template fallback) */
export function getBairroRioService(bairroSlug: string, serviceSlug: string): BairroService | undefined {
  const bairro = getBairroRioBySlug(bairroSlug);
  if (!bairro) return undefined;
  const explicit = bairro.services.find((s) => s.slug === serviceSlug);
  if (explicit) return explicit;
  if (ALL_SERVICE_SLUGS.includes(serviceSlug as ServiceSlug)) {
    return generateRioServiceFromTemplate(bairro.name, serviceSlug);
  }
  return undefined;
}

/** Get ALL services for a Rio bairro (explicit + template-generated) */
export function getAllBairroServicesRio(bairroSlug: string): BairroService[] {
  const bairro = getBairroRioBySlug(bairroSlug);
  if (!bairro) return [];
  return ALL_SERVICE_SLUGS.map((slug) => {
    const explicit = bairro.services.find((s) => s.slug === slug);
    if (explicit) return explicit;
    return generateRioServiceFromTemplate(bairro.name, slug)!;
  }).filter(Boolean);
}

/* ─── Neighbor Bairros for "Áreas Atendidas Próximas" interlinks ─── */
export const NEIGHBOR_BAIRROS_RIO: Record<string, string[]> = {
  "barra-da-tijuca": ["jacarepagua", "leblon", "ipanema"],
  "copacabana": ["ipanema", "botafogo", "leblon"],
  "ipanema": ["copacabana", "leblon", "botafogo"],
  "leblon": ["ipanema", "barra-da-tijuca", "copacabana"],
  "tijuca": ["madureira", "centro", "botafogo"],
  "botafogo": ["flamengo", "copacabana", "tijuca"],
  "flamengo": ["botafogo", "centro", "copacabana"],
  "centro": ["flamengo", "tijuca", "madureira"],
  "madureira": ["jacarepagua", "tijuca", "centro"],
  "jacarepagua": ["barra-da-tijuca", "madureira", "tijuca"],
};

/** Get neighbor bairros that have pages (filtered to only existing slugs) */
export function getNeighborBairrosRio(bairroSlug: string): Bairro[] {
  const neighborSlugs = NEIGHBOR_BAIRROS_RIO[bairroSlug] || [];
  return neighborSlugs
    .map((slug) => getBairroRioBySlug(slug))
    .filter((b): b is Bairro => b !== undefined);
}

/* ─── SEO Helper Functions for Rio ─── */

const bairroDescriptionPhrasesRio: Record<string, string> = {
  "barra-da-tijuca": "condomínios e casas próximos à lagoa",
  "copacabana": "prédios residenciais e comércios do bairro",
  "ipanema": "apartamentos e restaurantes da região",
  "leblon": "apartamentos de alto padrão e restaurantes",
  "tijuca": "casas e prédios próximos à floresta",
  "botafogo": "prédios residenciais e escritórios",
  "flamengo": "prédios tradicionais e residências",
  "centro": "empresas, restaurantes e prédios comerciais",
  "madureira": "casas e comércios da Zona Norte",
  "jacarepagua": "condomínios e casas próximos à mata",
};

const bairroH1PhrasesRio: Record<string, string> = {
  "barra-da-tijuca": "com atendimento rápido para condomínios",
  "copacabana": "com atendimento para prédios e comércios",
  "ipanema": "com atendimento premium para apartamentos",
  "leblon": "com atendimento VIP e discreto",
  "tijuca": "com atendimento para casas e prédios",
  "botafogo": "com atendimento para prédios e escritórios",
  "flamengo": "com atendimento para prédios tradicionais",
  "centro": "com atendimento para empresas e restaurantes",
  "madureira": "com equipe local e atendimento rápido",
  "jacarepagua": "com atendimento para condomínios e casas",
};

const bairroTitleVariationsRio: Record<string, { bairro: string; bairroServico: (svc: string) => string }> = {
  "barra-da-tijuca": { bairro: "Dedetizadora na Barra da Tijuca | Mais de 20 anos | USA", bairroServico: (svc) => `${svc} na Barra da Tijuca RJ | Orçamento Rápido | USA` },
  "copacabana": { bairro: "Dedetização em Copacabana RJ | Atendimento Rápido | USA", bairroServico: (svc) => `${svc} em Copacabana | Orçamento Grátis | Dedetizadora USA` },
  "ipanema": { bairro: "Dedetizadora em Ipanema | Garantia de Resultado | USA", bairroServico: (svc) => `${svc} em Ipanema RJ | Orçamento Rápido | USA` },
  "leblon": { bairro: "Dedetizadora no Leblon | Mais de 20 anos | USA", bairroServico: (svc) => `${svc} no Leblon RJ | Atendimento Rápido | USA` },
  "tijuca": { bairro: "Dedetização na Tijuca RJ | Resultado Garantido | USA", bairroServico: (svc) => `${svc} na Tijuca | Orçamento Grátis | Dedetizadora USA` },
  "botafogo": { bairro: "Dedetizadora em Botafogo | Orçamento Rápido | USA", bairroServico: (svc) => `${svc} em Botafogo RJ | Garantia de Resultado | USA` },
  "flamengo": { bairro: "Dedetização no Flamengo RJ | Mais de 20 anos | USA", bairroServico: (svc) => `${svc} no Flamengo | Atendimento Rápido | Dedetizadora USA` },
  "centro": { bairro: "Dedetizadora no Centro RJ | Resultado Garantido | USA", bairroServico: (svc) => `${svc} no Centro do Rio | Orçamento Rápido | USA` },
  
  "madureira": { bairro: "Dedetizadora em Madureira RJ | Mais de 20 anos | USA", bairroServico: (svc) => `${svc} em Madureira | Resultado Garantido | Dedetizadora USA` },
  "jacarepagua": { bairro: "Dedetizadora em Jacarepaguá RJ | Mais de 20 anos | USA", bairroServico: (svc) => `${svc} em Jacarepaguá | Orçamento Rápido | Dedetizadora USA` },
};

const bairroServiceDescVariationsRio: Record<string, (svc: string, bairro: string) => string> = {
  "barra-da-tijuca": (svc, b) => `${svc} na ${b} com equipe treinada. Mais de 20 anos de mercado e 50.000 clientes atendidos. Orçamento rápido no WhatsApp.`,
  "copacabana": (svc, b) => `${svc} profissional em ${b}. Atendemos prédios e comércios. Mais de 20 anos de experiência. Resultado satisfatório ou seu dinheiro de volta.`,
  "ipanema": (svc, b) => `${svc} em ${b} para apartamentos e comércios. Equipe treinada com mais de 50.000 atendimentos. Orçamento grátis: (21) 96850-0754.`,
  "leblon": (svc, b) => `${svc} no ${b} para apartamentos e restaurantes. Mais de 20 anos no mercado. Resultado satisfatório ou seu dinheiro de volta.`,
  "tijuca": (svc, b) => `${svc} na ${b} com atendimento especializado. Mais de 50.000 clientes. Equipe para casas e prédios. Orçamento rápido.`,
  "botafogo": (svc, b) => `${svc} em ${b} para prédios e escritórios. Mais de 20 anos de mercado. Resultado satisfatório ou seu dinheiro de volta. (21) 96850-0754.`,
  "flamengo": (svc, b) => `${svc} no ${b} com equipe especializada. Atendemos prédios tradicionais e modernos. 50.000+ clientes. Orçamento grátis pelo WhatsApp.`,
  "centro": (svc, b) => `${svc} no ${b} do Rio para empresas e restaurantes. Atendimento noturno. Mais de 20 anos de experiência. Certificação sanitária.`,
  "madureira": (svc, b) => `${svc} em ${b} para casas e comércios. Equipe local com mais de 20 anos. 50.000+ clientes atendidos. Orçamento rápido.`,
  "jacarepagua": (svc, b) => `${svc} em ${b} para condomínios e casas. Mais de 50.000 atendimentos. Resultado satisfatório ou seu dinheiro de volta.`,
};

const bairroServiceH1VariationsRio: Record<string, (svc: string, bairro: string) => string> = {
  "barra-da-tijuca": (svc, b) => `${svc} na ${b} — atendimento rápido para seu condomínio`,
  "copacabana": (svc, b) => `${svc} em ${b} — equipe especializada em prédios`,
  "ipanema": (svc, b) => `${svc} em ${b} — proteção para apartamentos e comércios`,
  "leblon": (svc, b) => `${svc} no ${b} — serviço premium e discreto`,
  "tijuca": (svc, b) => `${svc} na ${b} — equipe preparada para casas e prédios`,
  "botafogo": (svc, b) => `${svc} em ${b} — solução rápida para prédios`,
  "flamengo": (svc, b) => `${svc} no ${b} — proteção para prédios tradicionais`,
  "centro": (svc, b) => `${svc} no ${b} do Rio — atendimento para empresas`,
  "madureira": (svc, b) => `${svc} em ${b} — equipe local com atendimento rápido`,
  "jacarepagua": (svc, b) => `${svc} em ${b} — atendimento para condomínios e casas`,
};

export function getLocalSeoTitleRio(
  type: "hub" | "bairro" | "bairro-servico",
  bairroName?: string,
  servicoSlug?: string,
  bairroSlug?: string
): string {
  if (type === "hub") {
    return "Dedetização no Rio de Janeiro | Atendimento por Bairro | Dedetizadora USA";
  }
  if (type === "bairro" && bairroName) {
    const variation = bairroSlug ? bairroTitleVariationsRio[bairroSlug] : undefined;
    if (variation) return variation.bairro;
    return `Dedetização em ${bairroName} RJ | Garantia de Resultado | USA`;
  }
  if (type === "bairro-servico" && bairroName && servicoSlug) {
    const label = SERVICE_LABELS[servicoSlug]?.label || "Dedetização";
    const variation = bairroSlug ? bairroTitleVariationsRio[bairroSlug] : undefined;
    if (variation) return variation.bairroServico(label);
    return `${label} em ${bairroName} RJ | Orçamento Rápido | USA`;
  }
  return "Dedetizadora USA - Ultra Serviços Ambientais";
}

export function getLocalSeoDescriptionRio(
  type: "hub" | "bairro" | "bairro-servico",
  bairroName?: string,
  servicoSlug?: string,
  bairroSlug?: string
): string {
  if (type === "hub") {
    return "Encontre seu bairro e solicite orçamento rápido. Dedetizadora no Rio de Janeiro com mais de 20 anos de mercado e 50.000 clientes atendidos. (21) 96850-0754.";
  }
  const phrase = bairroSlug ? (bairroDescriptionPhrasesRio[bairroSlug] || "imóveis da região") : "imóveis da região";
  if (type === "bairro" && bairroName) {
    return `Atendimento rápido em ${bairroName} para ${phrase}. Mais de 20 anos de mercado e 50.000 clientes. Resultado satisfatório ou seu dinheiro de volta.`;
  }
  if (type === "bairro-servico" && bairroName && servicoSlug) {
    const label = SERVICE_LABELS[servicoSlug]?.label || "Dedetização";
    const variationFn = bairroSlug ? bairroServiceDescVariationsRio[bairroSlug] : undefined;
    if (variationFn) return variationFn(label, bairroName);
    return `${label} em ${bairroName} para ${phrase}. Mais de 20 anos de experiência. Resultado satisfatório ou seu dinheiro de volta. Orçamento grátis: (21) 96850-0754.`;
  }
  return "Dedetizadora USA no Rio de Janeiro. Mais de 20 anos de mercado e 50.000 clientes atendidos. (21) 96850-0754.";
}

export function getLocalH1Rio(
  type: "hub" | "bairro" | "bairro-servico",
  bairroName?: string,
  servicoSlug?: string,
  bairroSlug?: string
): string {
  if (type === "hub") {
    return "Controle de pragas no Rio de Janeiro — encontre seu bairro";
  }
  if (type === "bairro" && bairroName) {
    const phrase = bairroSlug ? (bairroH1PhrasesRio[bairroSlug] || "com atendimento rápido") : "com atendimento rápido";
    return `Dedetização em ${bairroName} ${phrase}`;
  }
  if (type === "bairro-servico" && bairroName && servicoSlug) {
    const label = SERVICE_LABELS[servicoSlug]?.label || "Dedetização";
    const variationFn = bairroSlug ? bairroServiceH1VariationsRio[bairroSlug] : undefined;
    if (variationFn) return variationFn(label, bairroName);
    return `${label} em ${bairroName} com garantia de resultado`;
  }
  return "Dedetizadora USA - Ultra Serviços Ambientais";
}

export const DECISION_SNIPPETS_RIO = {
  hub: "Encontre seu bairro e solicite orçamento rápido. Mais de 20 anos de mercado, mais de 50.000 clientes atendidos e garantia de resultado.",
  bairro: (bairro: string) => `Atendimento rápido em ${bairro} com garantia de resultado. Equipe treinada para residências e empresas.`,
  bairroServico: (svc: string, bairro: string) => `Se você precisa de ${svc.toLowerCase()} em ${bairro}, fale agora com a Dedetizadora USA. Mais de 20 anos de mercado, mais de 50.000 clientes atendidos e garantia de resultado.`,
};

/** Build WhatsApp URL for Rio */
export function buildWhatsAppUrlRio(origin?: string): string {
  const originParam = origin || window.location.pathname;
  const isServicePage = /\/(dedetizacao|desratizacao|descupinizacao|sanitizacao|formigas|pulgas|aranhas|pombos|caixa-dagua|caixa-gordura|fog|anti-tracas)/.test(originParam);
  const serviceMsg = isServicePage
    ? "Olá, vim do site e gostaria de um orçamento gratuito de dedetização! (origem=" + originParam + ")"
    : "Olá, vim do site e gostaria de um orçamento gratuito! (origem=" + originParam + ")";
  const msg = encodeURIComponent(serviceMsg);
  return `https://wa.me/${RIO_PHONE.replace("+", "")}?text=${msg}`;
}
